// src/store/slices/branchDisplaySlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  getBranchDisplayThunk,
  updateBrandingThunk,
  updateBrandColorsThunk,
  updateVisibilityThunk,
  uploadLogoThunk,
  deleteLogoThunk,
  uploadBannerThunk,
  getStoreImagesThunk,
  uploadStoreImagesThunk,
  updateStoreImageThunk,
  deleteStoreImageThunk,
  reorderStoreImagesThunk,
  setPrimaryImageThunk,
  getStoreVideosThunk,
  addStoreVideoThunk,
  updateStoreVideoThunk,
  deleteStoreVideoThunk,
  getHighlightsThunk,
  updateHighlightsThunk,
  addHighlightThunk,
  deleteHighlightThunk,
  getCollectionsThunk,
  updateCollectionsThunk,
  getServicesThunk,
  updateServicesThunk,
  addServiceThunk,
  updateServiceThunk,
  deleteServiceThunk,
  getCertificationsThunk,
  addCertificationThunk,
  updateCertificationThunk,
  deleteCertificationThunk,
  getAwardsThunk,
  addAwardThunk,
  updateAwardThunk,
  deleteAwardThunk,
  getTestimonialsThunk,
  addTestimonialThunk,
  updateTestimonialThunk,
  deleteTestimonialThunk,
  updateUSPsThunk,
  getContactInfoThunk,
  updateContactInfoThunk,
  updateSocialMediaThunk,
  getTeamMembersThunk,
  addTeamMemberThunk,
  updateTeamMemberThunk,
  deleteTeamMemberThunk,
  getStoreInfoThunk,
  updateStoreInfoThunk,
  updateFacilitiesThunk,
  updatePaymentMethodsThunk,
  updateDirectionsThunk,
  updateStoreDirectionsThunk,
  updateAddressThunk,
  getSEOThunk,
  updateSEOThunk,
  copyDisplayConfigThunk,
  getDisplayOptionsThunk,
  clearBranchDisplayError,
  resetBranchDisplay,
  setActiveSection,
  setEditMode,
} from "../thunks/branchDisplayThunks";

const initialState = {
  // Current branch display data
  currentBranchCode: null,
  display: null,
  contactInfo: null,
  storeInfo: null,
  seo: null,
  address: null, // Branch address with coordinates

  // Individual section data
  images: [],
  videos: [],
  highlights: [],
  collections: [],
  services: [],
  certifications: [],
  awards: [],
  testimonials: [],
  teamMembers: [],

  // Options/Enums from server
  displayOptions: null,

  // UI State
  activeSection: "branding", // branding, images, videos, highlights, etc.
  isEditMode: false,

  // Loading states
  loading: false,
  loadingSection: null, // Which section is currently loading
  uploading: false,

  // Error handling
  error: null,
  sectionError: null,

  // Success messages
  successMessage: null,
};

const branchDisplaySlice = createSlice({
  name: "branchDisplay",
  initialState,
  reducers: {
    setCurrentBranchCode: (state, action) => {
      state.currentBranchCode = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    updateLocalDisplay: (state, action) => {
      state.display = { ...state.display, ...action.payload };
    },
    updateLocalContactInfo: (state, action) => {
      state.contactInfo = { ...state.contactInfo, ...action.payload };
    },
    updateLocalStoreInfo: (state, action) => {
      state.storeInfo = { ...state.storeInfo, ...action.payload };
    },
    updateLocalSEO: (state, action) => {
      state.seo = { ...state.seo, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Clear error
      .addCase(clearBranchDisplayError, (state) => {
        state.error = null;
        state.sectionError = null;
      })

      // Reset state
      .addCase(resetBranchDisplay, (state) => {
        return { ...initialState, displayOptions: state.displayOptions };
      })

      // Set active section
      .addCase(setActiveSection, (state, action) => {
        state.activeSection = action.payload;
      })

      // Set edit mode
      .addCase(setEditMode, (state, action) => {
        state.isEditMode = action.payload;
      })

      // ============================================
      // GET BRANCH DISPLAY
      // ============================================
      .addCase(getBranchDisplayThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBranchDisplayThunk.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload.data;
        state.currentBranchCode = data.branchCode;
        state.display = data.display || {};
        state.contactInfo = data.contactInfo || {};
        state.storeInfo = data.storeInfo || {};
        state.seo = data.seo || {};
        state.address = data.address || {};

        // Extract arrays from display
        state.images = data.display?.storeImages || [];
        state.videos = data.display?.videos || [];
        state.highlights = data.display?.highlights || [];
        state.collections = data.display?.collections || [];
        state.services = data.display?.services || [];
        state.certifications = data.display?.certifications || [];
        state.awards = data.display?.awards || [];
        state.testimonials = data.display?.testimonials || [];
        state.teamMembers = data.contactInfo?.teamMembers || [];
      })
      .addCase(getBranchDisplayThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============================================
      // BRANDING
      // ============================================
      .addCase(updateBrandingThunk.pending, (state) => {
        state.loadingSection = "branding";
        state.sectionError = null;
      })
      .addCase(updateBrandingThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.display = {
          ...state.display,
          ...action.payload.data,
        };
        state.successMessage = "Branding updated successfully";
      })
      .addCase(updateBrandingThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // ============================================
      // BRAND COLORS
      // ============================================
      .addCase(updateBrandColorsThunk.pending, (state) => {
        state.loadingSection = "brandColors";
      })
      .addCase(updateBrandColorsThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.display = {
          ...state.display,
          brandColors: action.payload.data.brandColors,
        };
        state.successMessage = "Brand colors updated successfully";
      })
      .addCase(updateBrandColorsThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // ============================================
      // VISIBILITY
      // ============================================
      .addCase(updateVisibilityThunk.pending, (state) => {
        state.loadingSection = "visibility";
      })
      .addCase(updateVisibilityThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.display = {
          ...state.display,
          ...action.payload.data.visibility,
        };
        state.successMessage = "Visibility settings updated successfully";
      })
      .addCase(updateVisibilityThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // ============================================
      // LOGO
      // ============================================
      .addCase(uploadLogoThunk.pending, (state) => {
        state.uploading = true;
        state.loadingSection = "logo";
      })
      .addCase(uploadLogoThunk.fulfilled, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.display = {
          ...state.display,
          logo: action.payload.data.logo,
        };
        state.successMessage = "Logo uploaded successfully";
      })
      .addCase(uploadLogoThunk.rejected, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(deleteLogoThunk.pending, (state) => {
        state.loadingSection = "logo";
      })
      .addCase(deleteLogoThunk.fulfilled, (state) => {
        state.loadingSection = null;
        state.display = {
          ...state.display,
          logo: null,
        };
        state.successMessage = "Logo deleted successfully";
      })
      .addCase(deleteLogoThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // ============================================
      // BANNER
      // ============================================
      .addCase(uploadBannerThunk.pending, (state) => {
        state.uploading = true;
        state.loadingSection = "banner";
      })
      .addCase(uploadBannerThunk.fulfilled, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.display = {
          ...state.display,
          bannerImage: action.payload.data.bannerImage,
        };
        state.successMessage = "Banner uploaded successfully";
      })
      .addCase(uploadBannerThunk.rejected, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // ============================================
      // STORE IMAGES
      // ============================================
      .addCase(getStoreImagesThunk.pending, (state) => {
        state.loadingSection = "images";
      })
      .addCase(getStoreImagesThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.images = action.payload.data.images || [];
      })
      .addCase(getStoreImagesThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(uploadStoreImagesThunk.pending, (state) => {
        state.uploading = true;
        state.loadingSection = "images";
      })
      .addCase(uploadStoreImagesThunk.fulfilled, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        const uploadedImages = action.payload.data.uploadedImages || [];
        state.images = [...state.images, ...uploadedImages];
        state.successMessage = `${uploadedImages.length} image(s) uploaded successfully`;
      })
      .addCase(uploadStoreImagesThunk.rejected, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(updateStoreImageThunk.pending, (state) => {
        state.loadingSection = "images";
      })
      .addCase(updateStoreImageThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        const updatedImage = action.payload.data.image;
        const index = state.images.findIndex(
          (img) => img._id === updatedImage._id
        );
        if (index !== -1) {
          state.images[index] = updatedImage;
        }
        state.successMessage = "Image updated successfully";
      })
      .addCase(updateStoreImageThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(deleteStoreImageThunk.pending, (state) => {
        state.loadingSection = "images";
      })
      .addCase(deleteStoreImageThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.images = state.images.filter(
          (img) => img._id !== action.payload.imageId
        );
        state.successMessage = "Image deleted successfully";
      })
      .addCase(deleteStoreImageThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(reorderStoreImagesThunk.fulfilled, (state, action) => {
        state.images = action.payload.data.images || state.images;
        state.successMessage = "Images reordered successfully";
      })

      .addCase(setPrimaryImageThunk.fulfilled, (state, action) => {
        const primaryImage = action.payload.data.primaryImage;
        state.images = state.images.map((img) => ({
          ...img,
          isPrimary: img._id === primaryImage._id,
        }));
        state.successMessage = "Primary image set successfully";
      })

      // ============================================
      // STORE VIDEOS
      // ============================================
      .addCase(getStoreVideosThunk.pending, (state) => {
        state.loadingSection = "videos";
      })
      .addCase(getStoreVideosThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.videos = action.payload.data.videos || [];
      })
      .addCase(getStoreVideosThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(addStoreVideoThunk.pending, (state) => {
        state.loadingSection = "videos";
      })
      .addCase(addStoreVideoThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.videos.push(action.payload.data.video);
        state.successMessage = "Video added successfully";
      })
      .addCase(addStoreVideoThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(updateStoreVideoThunk.fulfilled, (state, action) => {
        const updatedVideo = action.payload.data.video;
        const index = state.videos.findIndex(
          (_, i) => i === action.meta.arg.videoIndex
        );
        if (index !== -1) {
          state.videos[index] = updatedVideo;
        }
        state.successMessage = "Video updated successfully";
      })

      .addCase(deleteStoreVideoThunk.fulfilled, (state, action) => {
        state.videos.splice(action.payload.videoIndex, 1);
        state.successMessage = "Video deleted successfully";
      })

      // ============================================
      // HIGHLIGHTS
      // ============================================
      .addCase(getHighlightsThunk.fulfilled, (state, action) => {
        state.highlights = action.payload.data.highlights || [];
      })

      .addCase(updateHighlightsThunk.pending, (state) => {
        state.loadingSection = "highlights";
      })
      .addCase(updateHighlightsThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.highlights = action.payload.data.highlights || [];
        state.successMessage = "Highlights updated successfully";
      })
      .addCase(updateHighlightsThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(addHighlightThunk.fulfilled, (state, action) => {
        state.highlights.push(action.payload.data.highlight);
        state.successMessage = "Highlight added successfully";
      })

      .addCase(deleteHighlightThunk.fulfilled, (state, action) => {
        state.highlights.splice(action.payload.highlightIndex, 1);
        state.successMessage = "Highlight deleted successfully";
      })

      // ============================================
      // COLLECTIONS
      // ============================================
      .addCase(getCollectionsThunk.fulfilled, (state, action) => {
        state.collections = action.payload.data.collections || [];
      })

      .addCase(updateCollectionsThunk.pending, (state) => {
        state.loadingSection = "collections";
      })
      .addCase(updateCollectionsThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.collections = action.payload.data.collections || [];
        state.successMessage = "Collections updated successfully";
      })
      .addCase(updateCollectionsThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // ============================================
      // SERVICES
      // ============================================
      .addCase(getServicesThunk.fulfilled, (state, action) => {
        state.services = action.payload.data.services || [];
      })

      .addCase(updateServicesThunk.pending, (state) => {
        state.loadingSection = "services";
      })
      .addCase(updateServicesThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.services = action.payload.data.services || [];
        state.successMessage = "Services updated successfully";
      })
      .addCase(updateServicesThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(addServiceThunk.fulfilled, (state, action) => {
        state.services.push(action.payload.data.service);
        state.successMessage = "Service added successfully";
      })

      .addCase(updateServiceThunk.fulfilled, (state, action) => {
        const updatedService = action.payload.data.service;
        const index = state.services.findIndex(
          (s) => s._id === updatedService._id
        );
        if (index !== -1) {
          state.services[index] = updatedService;
        }
        state.successMessage = "Service updated successfully";
      })

      .addCase(deleteServiceThunk.fulfilled, (state, action) => {
        state.services = state.services.filter(
          (s) => s._id !== action.payload.serviceId
        );
        state.successMessage = "Service deleted successfully";
      })

      // ============================================
      // CERTIFICATIONS
      // ============================================
      .addCase(getCertificationsThunk.fulfilled, (state, action) => {
        state.certifications = action.payload.data.certifications || [];
      })

      .addCase(addCertificationThunk.pending, (state) => {
        state.uploading = true;
        state.loadingSection = "certifications";
      })
      .addCase(addCertificationThunk.fulfilled, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.certifications.push(action.payload.data.certification);
        state.successMessage = "Certification added successfully";
      })
      .addCase(addCertificationThunk.rejected, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(updateCertificationThunk.fulfilled, (state, action) => {
        const updated = action.payload.data.certification;
        const index = state.certifications.findIndex(
          (c) => c._id === updated._id
        );
        if (index !== -1) {
          state.certifications[index] = updated;
        }
        state.successMessage = "Certification updated successfully";
      })

      .addCase(deleteCertificationThunk.fulfilled, (state, action) => {
        state.certifications = state.certifications.filter(
          (c) => c._id !== action.payload.certificationId
        );
        state.successMessage = "Certification deleted successfully";
      })

      // ============================================
      // AWARDS
      // ============================================
      .addCase(getAwardsThunk.fulfilled, (state, action) => {
        state.awards = action.payload.data.awards || [];
      })

      .addCase(addAwardThunk.pending, (state) => {
        state.uploading = true;
        state.loadingSection = "awards";
      })
      .addCase(addAwardThunk.fulfilled, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.awards.push(action.payload.data.award);
        state.successMessage = "Award added successfully";
      })
      .addCase(addAwardThunk.rejected, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(updateAwardThunk.fulfilled, (state, action) => {
        const updated = action.payload.data.award;
        const index = state.awards.findIndex((a) => a._id === updated._id);
        if (index !== -1) {
          state.awards[index] = updated;
        }
        state.successMessage = "Award updated successfully";
      })

      .addCase(deleteAwardThunk.fulfilled, (state, action) => {
        state.awards = state.awards.filter(
          (a) => a._id !== action.payload.awardId
        );
        state.successMessage = "Award deleted successfully";
      })

      // ============================================
      // TESTIMONIALS
      // ============================================
      .addCase(getTestimonialsThunk.fulfilled, (state, action) => {
        state.testimonials = action.payload.data.testimonials || [];
      })

      .addCase(addTestimonialThunk.pending, (state) => {
        state.uploading = true;
        state.loadingSection = "testimonials";
      })
      .addCase(addTestimonialThunk.fulfilled, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.testimonials.push(action.payload.data.testimonial);
        state.successMessage = "Testimonial added successfully";
      })
      .addCase(addTestimonialThunk.rejected, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(updateTestimonialThunk.fulfilled, (state, action) => {
        const updated = action.payload.data.testimonial;
        const index = action.meta.arg.testimonialIndex;
        if (index !== -1 && state.testimonials[index]) {
          state.testimonials[index] = updated;
        }
        state.successMessage = "Testimonial updated successfully";
      })

      .addCase(deleteTestimonialThunk.fulfilled, (state, action) => {
        state.testimonials.splice(action.payload.testimonialIndex, 1);
        state.successMessage = "Testimonial deleted successfully";
      })

      // ============================================
      // USPs
      // ============================================
      .addCase(updateUSPsThunk.pending, (state) => {
        state.loadingSection = "usps";
      })
      .addCase(updateUSPsThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.display = {
          ...state.display,
          usps: action.payload.data.usps,
        };
        state.successMessage = "USPs updated successfully";
      })
      .addCase(updateUSPsThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // ============================================
      // CONTACT INFO
      // ============================================
      .addCase(getContactInfoThunk.fulfilled, (state, action) => {
        state.contactInfo = action.payload.data.contactInfo || {};
      })

      .addCase(updateContactInfoThunk.pending, (state) => {
        state.loadingSection = "contactInfo";
      })
      .addCase(updateContactInfoThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.contactInfo = action.payload.data.contactInfo || {};
        state.successMessage = "Contact info updated successfully";
      })
      .addCase(updateContactInfoThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(updateSocialMediaThunk.fulfilled, (state, action) => {
        state.contactInfo = {
          ...state.contactInfo,
          socialMedia: action.payload.data.socialMedia,
        };
        state.successMessage = "Social media links updated successfully";
      })

      // ============================================
      // TEAM MEMBERS
      // ============================================
      .addCase(getTeamMembersThunk.fulfilled, (state, action) => {
        state.teamMembers = action.payload.data.teamMembers || [];
      })

      .addCase(addTeamMemberThunk.pending, (state) => {
        state.uploading = true;
        state.loadingSection = "team";
      })
      .addCase(addTeamMemberThunk.fulfilled, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.teamMembers.push(action.payload.data.teamMember);
        state.successMessage = "Team member added successfully";
      })
      .addCase(addTeamMemberThunk.rejected, (state, action) => {
        state.uploading = false;
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(updateTeamMemberThunk.fulfilled, (state, action) => {
        const updated = action.payload.data.teamMember;
        const index = state.teamMembers.findIndex((m) => m._id === updated._id);
        if (index !== -1) {
          state.teamMembers[index] = updated;
        }
        state.successMessage = "Team member updated successfully";
      })

      .addCase(deleteTeamMemberThunk.fulfilled, (state, action) => {
        state.teamMembers = state.teamMembers.filter(
          (m) => m._id !== action.payload.memberId
        );
        state.successMessage = "Team member deleted successfully";
      })

      // ============================================
      // STORE INFO
      // ============================================
      .addCase(getStoreInfoThunk.fulfilled, (state, action) => {
        state.storeInfo = action.payload.data.storeInfo || {};
      })

      .addCase(updateStoreInfoThunk.pending, (state) => {
        state.loadingSection = "storeInfo";
      })
      .addCase(updateStoreInfoThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.storeInfo = action.payload.data.storeInfo || {};
        state.successMessage = "Store info updated successfully";
      })
      .addCase(updateStoreInfoThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      .addCase(updateFacilitiesThunk.fulfilled, (state, action) => {
        state.storeInfo = {
          ...state.storeInfo,
          facilities: action.payload.data.facilities,
        };
        state.successMessage = "Facilities updated successfully";
      })

      .addCase(updatePaymentMethodsThunk.fulfilled, (state, action) => {
        state.storeInfo = {
          ...state.storeInfo,
          paymentMethods: action.payload.data.paymentMethods,
        };
        state.successMessage = "Payment methods updated successfully";
      })

      .addCase(updateDirectionsThunk.fulfilled, (state, action) => {
        state.storeInfo = {
          ...state.storeInfo,
          directions: action.payload.data.directions,
          nearbyLandmarks: action.payload.data.nearbyLandmarks,
          parking: action.payload.data.parking,
        };
        state.successMessage = "Directions updated successfully";
      })

      // updateStoreDirectionsThunk (alias with same handlers)
      .addCase(updateStoreDirectionsThunk.pending, (state) => {
        state.loadingSection = "directions";
      })
      .addCase(updateStoreDirectionsThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.storeInfo = {
          ...state.storeInfo,
          directions: action.payload.data.directions,
          nearbyLandmarks: action.payload.data.nearbyLandmarks,
          parking: action.payload.data.parking,
        };
        state.successMessage = "Location info updated successfully";
      })
      .addCase(updateStoreDirectionsThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // Full Address Update
      .addCase(updateAddressThunk.pending, (state) => {
        state.loadingSection = "address";
      })
      .addCase(updateAddressThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.address = action.payload.data.address || action.payload.data;
        state.successMessage = "Address updated successfully";
      })
      .addCase(updateAddressThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // ============================================
      // SEO
      // ============================================
      .addCase(getSEOThunk.fulfilled, (state, action) => {
        state.seo = action.payload.data.seo || {};
      })

      .addCase(updateSEOThunk.pending, (state) => {
        state.loadingSection = "seo";
      })
      .addCase(updateSEOThunk.fulfilled, (state, action) => {
        state.loadingSection = null;
        state.seo = action.payload.data.seo || {};
        state.successMessage = "SEO settings updated successfully";
      })
      .addCase(updateSEOThunk.rejected, (state, action) => {
        state.loadingSection = null;
        state.sectionError = action.payload;
      })

      // ============================================
      // BULK OPERATIONS
      // ============================================
      .addCase(copyDisplayConfigThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(copyDisplayConfigThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(copyDisplayConfigThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ============================================
      // DISPLAY OPTIONS
      // ============================================
      .addCase(getDisplayOptionsThunk.fulfilled, (state, action) => {
        state.displayOptions = action.payload.data;
      });
  },
});

export const {
  setCurrentBranchCode,
  clearSuccessMessage,
  updateLocalDisplay,
  updateLocalContactInfo,
  updateLocalStoreInfo,
  updateLocalSEO,
} = branchDisplaySlice.actions;

export default branchDisplaySlice.reducer;
